import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import Label from 'components/Label'
import React, { ChangeEvent, Fragment, useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  useFormContext,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { isDocumentFile } from 'utils/validate.util'
import { UploadButton } from './components/UploadButton'
import { StyledContainer } from './styles'
import { obterTamanhoArquivo, obterTipoArquivo, renderFileContent } from 'utils/base64.utils'
import CloseIcon from '@mui/icons-material/Close'
import DialogTitle from '@mui/material/DialogTitle'

interface InputFileProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>
  nameFilename: Path<TFieldValues>
  nameFiletype: Path<TFieldValues>
  control: Control<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  setError: UseFormSetError<TFieldValues>
  clearErrors: UseFormClearErrors<TFieldValues>
  disabled?: boolean
  onValidDocumentFile?: (file: File) => boolean
}

export function FormInputFile<TFieldValues extends FieldValues>({
  name,
  nameFilename,
  nameFiletype,
  control,
  setValue,
  setError,
  clearErrors,
  disabled,
  onValidDocumentFile,
}: InputFileProps<TFieldValues>) {
  const { watch } = useFormContext()

  const [openModal, setOpenModal] = useState(false)

  const nomeArquivo = watch(nameFilename)
  const tipoArquivo = watch(nameFiletype)
  const arquivo = watch(name)

  const effectiveIsDocumentFile = onValidDocumentFile ?? isDocumentFile

  useEffect(() => {
    if (!arquivo) {
      setValue(nameFilename, null as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(nameFiletype, null as PathValue<TFieldValues, Path<TFieldValues>>)
    }
  }, [arquivo, nameFiletype, nameFilename])

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files && e.target.files[0]

    if (file && effectiveIsDocumentFile(file)) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        if (reader && reader.result && typeof reader.result === 'string') {
          const base64String = reader.result
          setValue(
            name,
            base64String.split(',')[1] as PathValue<TFieldValues, Path<TFieldValues>>,
            { shouldDirty: true },
          )
          setValue(
            nameFiletype,
            obterTipoArquivo(base64String) as PathValue<TFieldValues, Path<TFieldValues>>,
            { shouldDirty: true },
          )
          setValue(nameFilename, file.name as PathValue<TFieldValues, Path<TFieldValues>>, {
            shouldDirty: true,
          })
          clearErrors(name)
        }
      }
      return
    }
    setValue(name, null as PathValue<TFieldValues, Path<TFieldValues>>)
    setError(name, { type: 'manual', message: SystemMessages.MESSAGE_FILE_OR_SIZE_FORMAT })
  }

  const handleReset = (): void => {
    setValue(name, null as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: false })
    clearErrors(name)
  }

  const handleOpenModal = () => setOpenModal(true)
  const handleCloseModal = () => setOpenModal(false)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value }, fieldState: { error } }) => (
        <Box display='flex' flexDirection='column' width='100%'>
          <Box display={'inline-flex'}>
            {!value ? (
              <UploadButton name={name} onChange={handleChange} disabled={disabled} />
            ) : (
              <StyledContainer>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  width={'100%'}
                  justifyContent={'space-between'}
                >
                  <IconButton size='small' onClick={handleOpenModal} title='Visualizar arquivo'>
                    <VisibilityIcon fontSize='small' />
                  </IconButton>

                  <Box display={'flex'} flexDirection={'column'}>
                    <Label text={nomeArquivo} bold noWrap sx={{ fontSize: 12, maxWidth: 128 }} />
                    <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                      <Label text={tipoArquivo} sx={{ fontSize: 10 }} bold />
                      <Label text={obterTamanhoArquivo(value)} sx={{ fontSize: 10 }} />
                    </Box>
                  </Box>
                  <IconButton
                    size='small'
                    sx={{ marginLeft: 2 }}
                    onClick={handleReset}
                    disabled={disabled}
                  >
                    <CancelRoundedIcon fontSize='small' />
                  </IconButton>
                </Box>
              </StyledContainer>
            )}
            <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth='md'>
              <DialogTitle>
                <Typography variant={'h6'} color={'primary.main'}>
                  Visualizar documento
                </Typography>
              </DialogTitle>
              <IconButton
                aria-label='close'
                onClick={handleCloseModal}
                sx={(theme) => ({
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent>{renderFileContent(nomeArquivo, arquivo, tipoArquivo)}</DialogContent>
            </Dialog>
          </Box>
          {error && error.message && (
            <Label text={error.message} sx={{ mt: 0.5, fontSize: 12, color: 'error.main' }} />
          )}
        </Box>
      )}
    />
  )
}

interface ViewFileProps {
  nomeArquivo?: string | null
  arquivo?: string | null
  tipoArquivo?: string | null

  disabled?: boolean
}

export const ViewFile = (props: ViewFileProps) => {
  const [openModal, setOpenModal] = useState(false)

  const handleOpenModal = () => setOpenModal(true)
  const handleCloseModal = () => setOpenModal(false)

  return (
    <Fragment>
      <StyledContainer>
        <Box display={'flex'} flexDirection={'row'} width={'100%'} gap={2}>
          <IconButton size='small' onClick={handleOpenModal} title='Visualizar arquivo'>
            <VisibilityIcon fontSize='small' />
          </IconButton>

          <Box display={'flex'} flexDirection={'column'}>
            <Label text={props.nomeArquivo} bold noWrap sx={{ fontSize: 12, maxWidth: 128 }} />
            <Box display={'flex'} flexDirection={'row'} gap={1}>
              <Label text={props.tipoArquivo} sx={{ fontSize: 10 }} bold />
              <Label text={obterTamanhoArquivo(props.arquivo)} sx={{ fontSize: 10 }} />
            </Box>
          </Box>
        </Box>
      </StyledContainer>
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth='md'>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            Visualizar documento
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleCloseModal}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {renderFileContent(props.nomeArquivo, props.arquivo, props.tipoArquivo)}
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}
